// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/components/home/StayCard.tsx
// 🔧 PURPOSE: La card del soggiorno, sovrapposta all'intestazione
//             (HomeMasthead lascia 92px, la card risale di 72).
//
//             - before (prima dell'arrivo): il riquadro espresso con i giorni
//               che mancano ("9 GIORNI", il giorno dell'arrivo "OGGI"), le
//               date e "Check-in dalle 15:00 · 3 notti". A 14 giorni o meno,
//               la previsione del giorno di arrivo (tocco → il meteo);
//             - stay (soggiorno): le date, "Check-out lunedì entro le 10:00"
//               (il giorno della partenza "Check-out oggi entro le..."), e a
//               destra il meteo di adesso con il mare (tocco → il meteo);
//             - after (dopo la partenza): solo le date.
//
//             Sotto, quello che passa la vista (di solito QuickActions) e, nel
//             soggiorno e dopo, "Tutti i dettagli del soggiorno" → /my-stay.
//             In scuro la card sale a stone-800 con il bordo stone-700
//             (CardShell raised): stone-900 sulla notte non si vedrebbe.
// ============================================

import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ChevronRight,
  Cloud,
  CloudFog,
  CloudLightning,
  CloudRain,
  CloudSnow,
  CloudSun,
  Sun,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import type { HomeState } from '../../lib/homeState';
import { useCheckInForecast, useHomeWeather } from '../../hooks/useHomeWeather';
import { CardShell, FOCUS_RING, INK_TEXT, MUTED_TEXT } from './ui';
import { formatDayKey, formatStayRange } from './format';

/** Il tuo piano di Esplora: meteo, idee e budget (la card "Il tuo piano" di DiscoverRail). */
export const PLANNER_PATH = '/explore?view=planner';
/** Il meteo di Esplora, dove portano il meteo e la previsione della card. */
export const WEATHER_PATH = '/explore?view=weather';

const WEATHER_ICONS: Record<string, LucideIcon> = {
  clearSky: Sun,
  partlyCloudy: CloudSun,
  cloudy: Cloud,
  overcast: Cloud,
  foggy: CloudFog,
  drizzle: CloudRain,
  rain: CloudRain,
  showers: CloudRain,
  snow: CloudSnow,
  snowShowers: CloudSnow,
  thunderstorm: CloudLightning,
};

/** L'icona del tempo dalla chiave `weather.*` di Esplora (clearSky, rain...). */
export const WeatherIcon: React.FC<{ labelKey: string; className?: string }> = ({ labelKey, className }) => {
  const Icon = WEATHER_ICONS[labelKey] || Sun;
  return <Icon className={className} aria-hidden="true" />;
};

export type StayCardVariant = 'before' | 'stay' | 'after';

export interface StayCardProps {
  /** Lo stato della Home: date, contatori, orario di uscita. */
  state: HomeState;
  /** Quale card; di default quella della vista (guest e before → before). */
  variant?: StayCardVariant;
  /** Sotto le date: di solito <QuickActions/> (e i suoi pannelli). */
  children?: React.ReactNode;
  /** "Tutti i dettagli del soggiorno" in fondo; di default nel soggiorno e dopo. */
  detailsLink?: boolean;
  className?: string;
}

/** Il riquadro interno (crema; in scuro più scuro della card). */
const INNER = 'rounded-xl bg-nva-cream dark:bg-stone-900';
const LABEL = 'text-[10px] font-extrabold uppercase tracking-[0.14em] text-nva-muted dark:text-stone-400';

const StayCard: React.FC<StayCardProps> = ({ state, variant, children, detailsLink, className }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'it';
  const kind: StayCardVariant = variant || (state.view === 'stay' ? 'stay' : state.view === 'after' ? 'after' : 'before');
  const showDetails = detailsLink ?? kind !== 'before';

  // Il meteo solo nella card del soggiorno, la previsione solo prima.
  const weather = useHomeWeather(kind === 'stay');
  const forecast = useCheckInForecast(state.checkInDay, kind === 'before');

  const range = formatStayRange(state.checkInDay, state.checkOutDay, lang);
  const nights = state.totalNights !== null ? t('home.stay.nights', { count: state.totalNights }) : '';

  let line = '';
  if (kind === 'before') {
    line = state.isArrivalDay
      ? t('home.stay.checkinToday', { time: state.checkInTime })
      : nights ? t('home.stay.checkinFrom', { time: state.checkInTime, nights }) : '';
  } else if (kind === 'stay') {
    line = state.isCheckoutDay
      ? t('home.stay.checkoutToday', { time: state.checkoutTime })
      : state.checkOutDay
        ? t('home.stay.checkoutBy', { day: formatDayKey(state.checkOutDay, lang, { weekday: 'long' }), time: state.checkoutTime })
        : '';
  }

  const texts = (
    <div className="flex min-w-0 flex-col gap-px">
      <span className={LABEL}>{t('home.stay.eyebrow')}</span>
      {range ? <span className={cn('text-[15px] font-bold leading-snug', INK_TEXT)}>{range}</span> : null}
      {line ? <span className={cn('text-[12.5px] leading-snug', MUTED_TEXT)}>{line}</span> : null}
    </div>
  );

  return (
    <div className={cn('relative -mt-[72px] px-4', className)}>
      <CardShell raised className="flex flex-col gap-3 p-3.5">
        {kind === 'before' ? (
          <div className="flex items-center gap-3.5">
            <Countdown state={state} />
            {texts}
          </div>
        ) : (
          <div className="flex items-stretch gap-2">
            <div className={cn(INNER, 'flex min-w-0 flex-grow flex-col justify-center px-[11px] py-[9px]')}>{texts}</div>
            {kind === 'stay' && weather ? (
              <Link
                to={WEATHER_PATH}
                aria-label={[
                  `${t(`weather.${weather.labelKey}`)} ${weather.temp}°`,
                  weather.seaTemp !== null ? t('home.stay.sea', { temp: weather.seaTemp }) : '',
                  t('home.stay.openWeather'),
                ].filter(Boolean).join('. ')}
                className={cn(INNER, 'flex w-[84px] flex-shrink-0 flex-col items-center justify-center gap-px px-2 py-[9px]', FOCUS_RING)}
              >
                <WeatherIcon labelKey={weather.labelKey} className="h-5 w-5 text-nva-gold-text dark:text-nva-gold-dark" />
                <span className={cn('text-[16px] font-extrabold tabular-nums', INK_TEXT)}>{weather.temp}°</span>
                {weather.seaTemp !== null ? (
                  <span className={cn('text-[11px] tabular-nums', MUTED_TEXT)}>{t('home.stay.sea', { temp: weather.seaTemp })}</span>
                ) : null}
              </Link>
            ) : null}
          </div>
        )}

        {kind === 'before' && forecast ? (
          <Link
            to={WEATHER_PATH}
            className={cn(INNER, 'flex min-h-[44px] items-center gap-2.5 px-3 py-2', FOCUS_RING)}
            aria-label={`${t('home.stay.forecast', { date: formatDayKey(forecast.day, lang, { weekday: 'short', day: 'numeric', month: 'short' }) })}: ${t(`weather.${forecast.labelKey}`)} ${forecast.tempMax}°. ${t('home.stay.openWeather')}`}
          >
            <WeatherIcon labelKey={forecast.labelKey} className="h-[18px] w-[18px] flex-shrink-0 text-nva-gold-text dark:text-nva-gold-dark" />
            <span className={cn('min-w-0 flex-grow truncate text-[13px] font-semibold', INK_TEXT)}>
              {t('home.stay.forecast', { date: formatDayKey(forecast.day, lang, { weekday: 'short', day: 'numeric', month: 'short' }) })}
            </span>
            <span className={cn('text-[14px] font-extrabold tabular-nums', INK_TEXT)}>{forecast.tempMax}°</span>
            <ChevronRight className="h-4 w-4 flex-shrink-0 text-nva-muted dark:text-stone-500" aria-hidden="true" />
          </Link>
        ) : null}

        {children}

        {showDetails ? (
          <Link
            to="/my-stay"
            className={cn(
              '-mx-3.5 -mb-1 flex min-h-[44px] items-center justify-center gap-1 border-t border-nva-hairline pt-2 text-[13.5px] font-bold dark:border-stone-700',
              INK_TEXT,
              FOCUS_RING,
            )}
          >
            {t('home.stay.allDetails')}
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        ) : null}
      </CardShell>
    </div>
  );
};

/** Il riquadro espresso con i giorni che mancano ("9 GIORNI"), o "OGGI". */
const Countdown: React.FC<{ state: HomeState }> = ({ state }) => {
  const { t } = useTranslation();
  const days = state.daysToCheckIn;
  const today = state.isArrivalDay || days === 0;
  if (!today && (days === null || days < 0)) return null;
  return (
    <div className="flex h-[84px] w-[84px] flex-shrink-0 flex-col items-center justify-center rounded-2xl bg-nva-espresso dark:bg-stone-950">
      {today ? (
        <span className="px-1 text-center font-display text-[22px] font-bold uppercase leading-none text-nva-bone">
          {t('home.stay.today')}
        </span>
      ) : (
        <>
          <span className="font-display text-[36px] font-bold leading-none tabular-nums text-nva-bone">{days}</span>
          <span className="mt-0.5 text-[10px] font-extrabold uppercase tracking-[0.14em] text-nva-gold">
            {t('home.stay.daysLeft', { count: days as number })}
          </span>
        </>
      )}
    </div>
  );
};

export default StayCard;
